/**
 * The `Files` tab (RF-5): the chat's shared PDF attachments, newest first,
 * as a compact list rather than a grid, each row opening the PDF in the
 * browser's own viewer. No custom in-app PDF renderer (ISSUE-379 R7).
 *
 * fileName and byteSize now arrive resolved on `/chat/list-files` itself
 * (ISSUE-750 R3), through the same `resolvePdfDocumentDetails` source
 * `/message/list` reads, so this row and a chat's own message bubble can
 * never disagree about one file's name or size. Only the actual openable
 * URL still comes from a client-side call to media-api's
 * `/object/get-batch` (`useChatMediaUrls`), the one sanctioned way to turn
 * a mediaId into a servable URL.
 */
import { useChatListFiles } from "@sportsmet/messaging-sdk/private";
import { Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { FILES_TAB_EMPTY_BODY } from "../messages/chatExtrasCopy";
import { formatFileSize } from "../messages/fileSizeFormat";
import { fileMetaLine } from "./pinDisplay";
import { useChatMediaUrls } from "./useChatMediaUrls";

export type typeFilesTabProps = {
  chatId: string;
};

const FILES_PAGE_LIMIT = 200;

export const FilesTab = (props: typeFilesTabProps): ReactNode => {
  const chatListFiles = useChatListFiles({
    chatId: props.chatId,
    limit: FILES_PAGE_LIMIT,
  });

  const rows = chatListFiles.data?.data?.items ?? [];
  const resolved = useChatMediaUrls(rows.map((row) => row.mediaObjectId));

  if (chatListFiles.isLoading) {
    return (
      <div
        data-testid="chat-details-files-loading"
        className="flex flex-col gap-3"
      >
        <Skeleton shape="card" label="Loading files" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        data-testid="chat-details-files-empty"
        className="flex flex-col items-center gap-1 py-10 text-center"
      >
        <p className="text-body-strong text-ink">No files yet</p>
        <p className="text-body text-ink-secondary">{FILES_TAB_EMPTY_BODY}</p>
      </div>
    );
  }

  return (
    <ul data-testid="chat-details-files" className="flex flex-col gap-3">
      {rows.map((row) => {
        const media = resolved.get(row.mediaObjectId);
        return (
          <li
            key={row.messageId}
            data-testid="file-row"
            className="flex items-center gap-3 border-b border-hairline pb-3"
          >
            <span
              aria-hidden="true"
              className="grid size-10 shrink-0 place-items-center rounded-md bg-surface-secondary text-body-strong text-ink"
            >
              {/* A plain document glyph. The kit ships no icon set. */}
              PDF
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-body text-ink">
                {row.fileName}
              </span>
              <span className="text-caption text-ink-secondary">
                {typeof row.byteSize === "number"
                  ? `${fileMetaLine(row.sentAtIso, new Date())} · ${formatFileSize(row.byteSize)}`
                  : fileMetaLine(row.sentAtIso, new Date())}
              </span>
            </div>
            {media ? (
              <a
                href={media.originalUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="file-row-open"
                className="text-body-strong text-primary underline-offset-2 hover:underline"
              >
                Open
              </a>
            ) : (
              <span
                data-testid="file-row-unavailable"
                className="text-body text-ink-tertiary"
              >
                Unavailable
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};
